import { HttpClient } from '@angular/common/http';
import { Service, computed, inject, signal } from '@angular/core';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import { firstValueFrom } from 'rxjs';
import { environment } from '@environments/environment';
import { AppRole, Me } from '../api/me';

/** Текущий пользователь сервиса: данные GET /api/me, роль, выход. */
@Service()
export class CurrentUserService {
    private readonly http = inject(HttpClient);
    private readonly oidc = inject(OidcSecurityService);

    private readonly _me = signal<Me | null>(null);
    private loading: Promise<Me> | null = null;

    readonly me = this._me.asReadonly();
    readonly role = computed<AppRole | null>(() => this._me()?.role ?? null);
    readonly hasRole = computed(() => this._me()?.hasRole ?? false);
    readonly isAdmin = computed(()=>this._me()?.role === 'administrator')
    readonly organizations = computed(() => this._me()?.organizations ?? []);

    /** Загружает /api/me один раз (повторный вызов возвращает загруженное). */
    load(): Promise<Me> {
        const current = this._me();
        if (current) return Promise.resolve(current);
        this.loading ??= firstValueFrom(this.http.get<Me>(`${environment.apiUrl}/me`)).then((me) => {
            this._me.set(me);
            return me;
        }).finally(() => {
            this.loading = null;
        });
        return this.loading;
    }

    /** Принудительно перечитать /api/me (после назначения роли администратором). */
    async reload(): Promise<Me> {
        this._me.set(null);
        return this.load();
    }

    hasAnyRole(roles: readonly AppRole[]): boolean {
        const role = this.role();
        return role !== null && roles.includes(role);
    }


    /** Выход из Keycloak; после выхода Keycloak вернёт пользователя на портал. */
    logout(): void {
        this.oidc.logoff().subscribe();
    }
}
